import {Card, FormControl, FormField, Separator} from 'platform/components';
import {Grid, GridItem, Space, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {useGetSeriesListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {useDocumentTemplates} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormType} from '../../../types/ServiceOrderVariant';
import {getPaymentCardPrefill} from '../../../utils/getPaymentCardPrefill';
import {getPrimaryDefaultTemplateId} from '../../../utils/getPrimaryDefaultTemplateId';
import {getSoleSeriesId} from '../../../utils/getSoleSeriesId';

interface InternalPaymentProps extends RequiredTestIdProps {
  formApi: UseFormReturn<FormType>;
  control: FormControl<FormType>;
  title: string;
}

export function InternalPayment(props: InternalPaymentProps) {
  const isAllowed = useWatch({
    control: props.control,
    name: 'paymentType.internalPayment.isAllowed',
  });

  const {options, isLoading, soleDocSeriesId} = useGetSeriesListQuery(
    {
      type: ['accounting/invoice'],
    },
    {
      selectFromResult: (result) => ({
        ...result,
        options:
          result?.data?.map((serie) => ({
            label: serie.name,
            value: serie.id,
          })) ?? [],
        soleDocSeriesId: getSoleSeriesId(result?.data, 'accounting/invoice'),
      }),
    }
  );

  const [getTemplates, {isLoading: isTemplatesLoading}] = useDocumentTemplates();
  const {options: invoiceTemplateOptions, defaultTemplates} = getTemplates(
    'workshop-invoice_internal'
  );

  const primaryTemplateId = getPrimaryDefaultTemplateId(defaultTemplates);

  // Writes must validate: plain `setValue` does not revalidate under `mode="onChange"`, so the
  // card would keep the "required" errors it was just filled to resolve.
  const prefillCard = () => {
    const prefill = getPaymentCardPrefill({
      invoiceTemplateId: props.formApi.getValues('paymentType.internalPayment.invoice.templateId'),
      invoiceDocSeriesId: props.formApi.getValues(
        'paymentType.internalPayment.invoice.docSeriesId'
      ),
      dependentDocSeriesIds: {
        invoiceCorrection: props.formApi.getValues(
          'paymentType.internalPayment.invoiceCorrection.docSeriesId'
        ),
      },
      primaryTemplateId,
      soleDocSeriesId,
    });

    const templateId = prefill['invoice.templateId'];
    const docSeriesId = prefill['invoice.docSeriesId'];
    const correctionDocSeriesId = prefill['invoiceCorrection.docSeriesId'];

    if (isNotNil(templateId)) {
      props.formApi.setValue('paymentType.internalPayment.invoice.templateId', templateId, {
        shouldValidate: true,
      });
    }

    if (isNotNil(docSeriesId)) {
      props.formApi.setValue('paymentType.internalPayment.invoice.docSeriesId', docSeriesId, {
        shouldValidate: true,
      });
    }

    if (isNotNil(correctionDocSeriesId)) {
      props.formApi.setValue(
        'paymentType.internalPayment.invoiceCorrection.docSeriesId',
        correctionDocSeriesId,
        {shouldValidate: true}
      );
    }
  };

  return (
    <Card
      title={props.title}
      isExpanded={isAllowed}
      control={{
        type: 'switch',
        value: isAllowed,
        isDisabled: isLoading || isTemplatesLoading,
        onChange: (isEnabled) => {
          props.formApi.setValue('paymentType.internalPayment.isAllowed', isEnabled);

          if (isEnabled) {
            prefillCard();
          }
        },
      }}
      variant="inlineGrey"
      data-testid={suffixTestId('card', props)}
    >
      <Separator spacing={0} />
      <Space vertical={4} />
      <VStack spacing={4}>
        <Grid columns={2} horizontalSpacing={6}>
          <GridItem>
            <FormField
              control={props.control}
              name="paymentType.internalPayment.invoice.templateId"
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceTemplate')}
              isNotClearable
              options={invoiceTemplateOptions ?? []}
              isLoading={isTemplatesLoading}
              isRequired
              data-testid={suffixTestId('invoiceTemplate', props)}
            />
          </GridItem>
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name="paymentType.internalPayment.invoice.docSeriesId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.invoiceDocumentSerie')}
            isNotClearable
            options={options}
            isLoading={isLoading}
            isRequired
            data-testid={suffixTestId('invoiceDocumentSerie', props)}
          />
          <FormField
            control={props.control}
            name="paymentType.internalPayment.invoiceCorrection.docSeriesId"
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocument')}
            isNotClearable
            options={options}
            isLoading={isLoading}
            isRequired
            data-testid={suffixTestId('correctiveDocument', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name="paymentType.internalPayment.isAutoInitiatePayment"
            type="checkbox"
            label={i18n.t('entity.settings.labels.initiatePaymentAutomatically')}
            helperText={i18n.t('entity.settings.labels.initiatePaymentAutomaticallyHelper')}
            isDisabled
            data-testid={suffixTestId('initiatePaymentAutomatically', props)}
          />
        </Grid>
      </VStack>
    </Card>
  );
}
